import React from 'react';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import {
  autoDisconnectMutationObserver,
  customEvents,
  getFocusSelector,
  getSpecificParent,
  globalFocus,
  isChild,
  cantFocusSelector,
  scrollParentIntoElement,
  useLatest,
  useUpdateEffect,
} from '@apia/util';
import { ThrottleOptions } from 'ahooks/lib/useThrottle/throttleOptions';
import { useThrottleFn } from 'ahooks';
import { TUpdateByClickPayload } from './getReduxActions';
import { willHandleKey } from './handleKey';
import {
  INewFocus,
  ISelectableState,
  THandleKey,
  TKeyHandlerProps,
  TUseDomProps,
} from './types';
import {
  TResponsiveTableRow,
  TResponsiveTableRowsSelectionEvent,
} from '../../types';
import { responsiveTableStore, useResponsiveTable } from '..';

export function shoutKeyhandlerLive(
  liveType: 'reset' | 'howToStartDrag' | 'howToEndDrag' | 'whichIsTheNewIndex',
) {
  switch (liveType) {
    /* case 'howToEndDrag':
      AriaLiveEmitter.emit('live', {
        type: 'assertive',
        message: window.ARIA_HOW_TO_END_DRAG,
      });
      break;
    case 'whichIsTheNewIndex':
      AriaLiveEmitter.emit('live', {
        type: 'assertive',
     *         message:
     *         formatMessage(window.ARIA_HOW_TO_WHICH_IS_THE_NEW_POSITION, {
          TOK1: String(additionalData?.newIndex ?? 0),
        }),
      });
      break;
    case 'reset':
      AriaLiveEmitter.emit('live', {
        type: 'reset',
        message: '',
      });
      break;
    default:
      AriaLiveEmitter.emit('live', {
        type: 'assertive',
        message: window.ARIA_HOW_TO_START_DRAG,
      }); */
    default:
  }
}

let scrollTimeout: number;
/**
 * Devuelve un elemento KeyHandler que puede usarse como
 * en el ejemplo.
 *
 * Su función es la de manejar los eventos de teclado y
 * ejecutar las acciones necesarias en el estado de la
 * grilla de acuerdo a la funcionalidad estándar de
 * usabilidad de grillas.
 *
 * @example
 *
  <KeyHandler>
    <table ... />
  </KeyHandler>
 */
export function makeKeyHandler(
  actions: {
    handleKey: (payload: THandleKey & { id: string }) => {
      payload: THandleKey & { id: string };
      type: string;
    };
    selectionStateUpdate: (
      payload: Partial<ISelectableState> & { id: string },
    ) => {
      payload: Partial<ISelectableState> & { id: string };
      type: string;
    };
    setNewFocused: (payload: INewFocus & { id: string }) => {
      payload: INewFocus & { id: string };
      type: string;
    };
    swapFocusedRow?: (
      payload: {
        newIndex: number;
      } & {
        tableName: string;
      },
    ) => {
      payload: {
        newIndex: number;
      } & {
        tableName: string;
      };
      type: string;
    };
    updateByClick: (payload: TUpdateByClickPayload['payload']) => {
      payload: INewFocus & { id: string };
      type: string;
    };
  },
  id: string,
  config: { throttleOptions?: ThrottleOptions; stateSelector: TUseDomProps },
) {
  return function KeyHandler({
    children,
    onChangeSelection,
    onRowClick: onClickRows,
    onSelectRows,
    ...props
  }: TKeyHandlerProps) {
    const { run: keyHandler } = useThrottleFn<
      React.KeyboardEventHandler<HTMLDivElement>
    >(
      (ev) => {
        if (!['shift', 'alt', 'control'].includes(ev.key.toLowerCase())) {
          responsiveTableStore.dispatch(actions.handleKey({ id, ev }));
        }
      },
      config?.throttleOptions ?? { wait: 100 },
    );

    const [ref, setRef] = React.useState<HTMLElement | null>(null);

    const selectedRows = useResponsiveTable(
      (global) => {
        const state = config.stateSelector(global);
        return (state?.selectedRows ?? []).map<
          TResponsiveTableRowsSelectionEvent[0]
        >((current) => ({
          index: current,
          row: state.rows[current] as TResponsiveTableRow,
        }));
      },
      (a, b) =>
        shallowEqual(
          a.map((current) => current.index),
          b.map((current) => current.index),
        ),
    );
    const selectedRowsRef = useLatest(selectedRows);
    const { allowKeyboardSorting, focusedCell, isEditionMode, isFocused } =
      useResponsiveTable((global) => {
        const state = config.stateSelector(global);
        return {
          focusedCell:
            state?.focusedRow !== undefined
              ? `${state?.focusedRow}_${state?.focusedColumn}`
              : undefined,
          isEditionMode: state?.isEditionMode,
          rows: state?.rows,
          isFocused: state?.isFocused,
          allowKeyboardSorting: state?.allowRowsKeyboardSorting,
        };
      }, shallowEqual);
    useUpdateEffect(() => {
      if (onChangeSelection) onChangeSelection(selectedRows);
    }, [onChangeSelection, selectedRows]);

    const handleClick = React.useCallback(
      (ev: MouseEvent | FocusEvent | Event) => {
        if ((ev.target as HTMLElement).closest('.moreInformationButton'))
          return;

        if ((ev as MouseEvent).shiftKey) {
          document.getSelection()?.removeAllRanges();
        }
        const parentRow = getSpecificParent(
          ev.target as HTMLElement,
          (current) => {
            if (current.tagName.toLowerCase() === 'table') return null;
            if (current.matches('tr')) {
              return true;
            }
            return false;
          },
        );
        const parentCell = getSpecificParent(
          ev.target as HTMLElement,
          (current) => {
            if (current.tagName.toLowerCase() === 'table') return null;
            if (current.matches('td,th')) {
              return true;
            }
            return false;
          },
        );
        if (parentCell && parentRow) {
          responsiveTableStore.dispatch(
            actions.updateByClick({
              altKey: (ev as MouseEvent).altKey,
              id,
              ctrlKey: (ev as MouseEvent).ctrlKey,
              ariaColIndex: Number(parentCell.getAttribute('aria-colindex')),
              rowIndex: Number(parentRow.getAttribute('data-focusindex')),
              shiftKey: (ev as MouseEvent).shiftKey,
            }),
          );
          if (onClickRows && ev.type === 'mousedown')
            onClickRows(
              selectedRowsRef.current?.[0],
              config.stateSelector(responsiveTableStore.getState()).focusedRow,
            );
        }
      },
      [onClickRows, selectedRowsRef],
    );
    const handleDoubleClick = React.useCallback(
      (ev: MouseEvent) => {
        if (
          isChild(
            ev.target as HTMLElement,
            (current) => current.tagName === 'THEAD',
          )
        )
          return;
        ev.preventDefault();
        if (onSelectRows)
          onSelectRows(
            selectedRowsRef.current,
            config.stateSelector(responsiveTableStore.getState()).focusedRow,
          );
      },
      [onSelectRows, selectedRowsRef],
    );

    const initialized = React.useRef(true);

    const running = React.useRef(false);
    const doFocusThings = React.useCallback(() => {
      if (!running.current) {
        running.current = true;
        if (focusedCell === undefined) {
          running.current = false;
          return () => {};
        }

        if (ref) {
          const disconnectObserver = autoDisconnectMutationObserver(
            ref,
            () => {
              scrollTimeout = setTimeout(() => {
                const focusedElement = ref.querySelector(
                  '[data-focused="true"]',
                );

                let tableCellElement: HTMLElement | null = null;
                let tableCellWidget: HTMLElement | null = null;

                if (
                  ['td', 'th'].includes(
                    focusedElement?.tagName.toLowerCase() ?? '',
                  )
                ) {
                  tableCellElement = focusedElement as HTMLElement;
                  tableCellWidget = focusedElement?.querySelector(
                    getFocusSelector(':not(.debug_info__button)'),
                  ) as HTMLElement;
                }

                if (focusedElement?.tagName.toLowerCase() === 'tr') {
                  tableCellElement = focusedElement.querySelector('td,th');
                  tableCellWidget =
                    (focusedElement.querySelector(
                      getFocusSelector(':not(.debug_info__button)'),
                    ) as HTMLElement) || tableCellElement;
                }

                if (tableCellWidget) {
                  tableCellElement?.setAttribute('tabIndex', '-1');
                  tableCellWidget.setAttribute('tabIndex', '0');
                } else tableCellElement?.setAttribute('tabIndex', '0');

                if (!isEditionMode) {
                  const allFocusableElements = ref?.querySelectorAll(
                    getFocusSelector(
                      `${cantFocusSelector}:not([data-focused="true"])`,
                    ),
                  );
                  allFocusableElements?.forEach((current) => {
                    if (current !== tableCellWidget) {
                      current.setAttribute('tabIndex', '-1');
                    }
                    if (tableCellWidget?.tagName === 'BUTTON')
                      tableCellWidget.setAttribute('role', 'gridcell');
                  });
                } else {
                  const focusableElements =
                    tableCellElement?.querySelectorAll('[tabindex="-1"]');

                  focusableElements?.forEach((current) =>
                    current.setAttribute('tabIndex', '0'),
                  );
                }

                const focusElement = tableCellWidget ?? tableCellElement;
                const row = getSpecificParent(
                  focusElement,
                  (current) => current.tagName === 'TR',
                );

                if (
                  focusElement &&
                  initialized.current &&
                  config.stateSelector(responsiveTableStore.getState())
                    .isFocused
                ) {
                  globalFocus.focus = focusElement;

                  focusElement.focus();
                }

                if (row) {
                  scrollParentIntoElement(row, 55, 2, 100, 'makeKeyHandler');
                }

                if (!initialized.current) {
                  initialized.current = true;
                }
              }, 0) as unknown as number;
            },
            { timeout: 5000 },
          );

          running.current = false;
          return () => {
            disconnectObserver();
            clearTimeout(scrollTimeout);
          };
        }
        running.current = false;
      }
      return () => {};
    }, [focusedCell, ref, isEditionMode]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useLayoutEffect(doFocusThings, [
      focusedCell,
      isFocused,
      isEditionMode,
    ]);
    React.useEffect(() => {
      function handleFocus(ev: FocusEvent) {
        const targetTable = getSpecificParent(
          ev.target as HTMLElement,
          (current) => current.tagName.toLowerCase() === 'table',
        );
        const relatedTargetTable = getSpecificParent(
          ev.relatedTarget as HTMLElement,
          (current) => current.tagName.toLowerCase() === 'table',
        );

        if (!relatedTargetTable || relatedTargetTable !== targetTable)
          handleClick(ev);
      }

      ref?.addEventListener('dblclick', handleDoubleClick);
      ref?.addEventListener('mousedown', handleClick);
      ref?.addEventListener('focusin', handleFocus);
      ref?.addEventListener(customEvents.focus, handleClick);
      return () => {
        ref?.removeEventListener('mousedown', handleClick);
        ref?.removeEventListener('dblclick', handleDoubleClick);
        ref?.removeEventListener('focusin', handleFocus);
        ref?.removeEventListener(customEvents.focus, handleClick);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);

    const isDragging = React.useRef(false);
    const isFocusedRef = React.useRef(false);

    return (
      <Box
        ref={setRef}
        {...props}
        className={`selection__keyHandler ${props.className ?? ''}`}
        onBlur={React.useCallback(
          (ev: React.FocusEvent) => {
            if (
              !ev.relatedTarget ||
              !getSpecificParent(
                ev.relatedTarget as HTMLElement,
                (current) => current === ref,
              )
            ) {
              responsiveTableStore.dispatch(
                actions.selectionStateUpdate({ id, isFocused: false }),
              );
              shoutKeyhandlerLive('reset');
              isFocusedRef.current = false;
            }
          },
          [ref],
        )}
        onFocus={React.useCallback(() => {
          if (!isFocusedRef.current) shoutKeyhandlerLive('howToStartDrag');
          isFocusedRef.current = true;
        }, [])}
        sx={React.useMemo(
          () => ({ overflow: 'auto', position: 'relative' }),
          [],
        )}
        onKeyDown={React.useCallback<
          React.KeyboardEventHandler<HTMLDivElement>
        >(
          (ev) => {
            const state = config.stateSelector(responsiveTableStore.getState());

            if (
              willHandleKey(
                ev,
                config.stateSelector(responsiveTableStore.getState()),
              )
            ) {
              ev.preventDefault();
              ev.stopPropagation();
            }
            if (
              actions.swapFocusedRow &&
              isDragging.current &&
              allowKeyboardSorting
            ) {
              if (
                (ev.key === 'Enter' || ev.key === 'Escape') &&
                isDragging.current
              ) {
                shoutKeyhandlerLive('howToStartDrag');
                isDragging.current = false;
                responsiveTableStore.dispatch(
                  actions.swapFocusedRow({
                    tableName: id,
                    newIndex: state.focusedRow,
                  }),
                );
              } else if (['ArrowDown', 'ArrowUp'].includes(ev.key)) {
                if (ev.key === 'ArrowDown') {
                  responsiveTableStore.dispatch(
                    actions.swapFocusedRow({
                      tableName: id,
                      newIndex: state.focusedRow + 1,
                    }),
                  );
                } else {
                  responsiveTableStore.dispatch(
                    actions.swapFocusedRow({
                      tableName: id,
                      newIndex: state.focusedRow - 1,
                    }),
                  );
                }
              }
            } else if (
              ev.key === 'm' &&
              ev.ctrlKey &&
              !isDragging.current &&
              state.focusedRow >= 0 &&
              actions.swapFocusedRow
            ) {
              isDragging.current = true;
              shoutKeyhandlerLive('howToEndDrag');
              responsiveTableStore.dispatch(
                actions.swapFocusedRow({
                  tableName: id,
                  newIndex: state.focusedRow,
                }),
              );
            } else {
              const currentSelectedRows = (state?.selectedRows ?? []).map<
                TResponsiveTableRowsSelectionEvent[0]
              >((current) => ({
                index: current,
                row: state.rows[current] as TResponsiveTableRow,
              }));
              if (props.onKeyDown) props.onKeyDown(ev);
              if (ev.key === 'Enter' && onSelectRows && state.focusedRow >= 0) {
                onSelectRows(currentSelectedRows, state.focusedRow);
              }
              keyHandler(ev);
            }
          },
          [allowKeyboardSorting, keyHandler, onSelectRows, props],
        )}
      >
        {children}
      </Box>
    );
  };
}
